import { index, pgEnum, pgTable, text, uuid } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

import { usStatesEnum, usStatesEnumSchema } from "./commonSchema";
import { patientsTable } from "./patientsSchema";
import { customDate, customTimestamp } from "./utils/types";

export const redeterminationStatusEnumSchema = z.enum([
  "NOT_STARTED",
  "PENDING_FORM",
  "CALL_BACK",
  "COMPLETED",
]);
export type RedeterminationStatusEnumType = z.infer<
  typeof redeterminationStatusEnumSchema
>;
export const redeterminationStatusEnum = pgEnum(
  "redetermination_status_enum",
  redeterminationStatusEnumSchema.enum,
);

export const redeterminationsTable = pgTable(
  "redeterminations",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    eventId: text("event_id").notNull().unique(),
    memberId: text("member_id").notNull(),
    // Nullable: an event can arrive before its patient exists (holding record).
    patientId: uuid("patient_id").references(() => patientsTable.id, {
      onDelete: "cascade",
    }),
    workspaceId: text("workspace_id").notNull(),
    redeterminationDate: customDate("redetermination_date").notNull(),
    usState: usStatesEnum("us_state").notNull(),
    status: redeterminationStatusEnum("status")
      .notNull()
      .default("NOT_STARTED"),
    createdAt: customTimestamp("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: customTimestamp("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString())
      .$onUpdateFn(() => new Date().toISOString()),
  },
  table => [
    index("redeterminations_member_id_index").on(table.memberId),
    index("redeterminations_patient_id_index").on(table.patientId),
  ],
);

export const insertRedeterminationSchema = createInsertSchema(
  redeterminationsTable,
  {
    eventId: z.string().min(1),
    memberId: z.string().min(1),
    patientId: z.uuid().nullish(),
    workspaceId: z.string().min(1),
    redeterminationDate: z.iso.date(),
    usState: usStatesEnumSchema,
    status: redeterminationStatusEnumSchema.optional(),
  },
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertRedeterminationType = z.infer<
  typeof insertRedeterminationSchema
>;

export const redeterminationSchema = createSelectSchema(redeterminationsTable, {
  status: redeterminationStatusEnumSchema,
  usState: usStatesEnumSchema,
});
export type RedeterminationType = z.infer<typeof redeterminationSchema>;

// Only `status` is mutable through the patient-facing API; every other field
// is owned by the inbound webhook.
export const updateRedeterminationSchema = insertRedeterminationSchema
  .pick({ status: true })
  .partial();

export type UpdateRedeterminationType = z.infer<
  typeof updateRedeterminationSchema
>;
